// Angular
import { AfterViewInit, Component, OnDestroy, QueryList, ViewChildren, inject } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
// Material
import { MatDialogRef } from '@angular/material/dialog';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ClientGQLDisplay, CollectionsRecipientExclusion, CommunicationRecipientRow } from '@anduin/api';
import { LaunchDarklyFlag, LaunchDarklyService } from '@anduin/auth';
import { CollectionsMode, ConfigService } from '@anduin/config';
import {
  LoadableStatus,
  applicationDebounceTime,
  extractFailureContext,
  getControlValueChanges,
  getLoadedResults,
  ignoreEmpty,
  removeArrayItem,
  replaceArrayItem,
} from '@anduin/shared';
// NGXS
import { Store } from '@ngxs/store';
// RxJS
import { BehaviorSubject, Observable, Subject, combineLatest, defer, merge, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  skip,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { StatementRecipientLabel } from '../../models';
// Aiwyn
import {
  ClearCollectionsPageData,
  ClearExcludedClientsState,
  CommonCollectionsState,
  GetExcludedStatementRecipients,
  GetStaff,
  SearchExcludedStatementRecipients,
  SortExcludedStatementRecipients,
  UpdateCollectionsClientExclusionStatus,
  UpdateCollectionsSettings,
} from '../../state';
import { CollectionsExclusionsStep, CollectionsTrackService } from '../../tracking';

enum HeaderCheckboxStatus {
  UNCHECKED = 'UNCHECKED',
  CHECKED = 'CHECKED',
  INDETERMINATE = 'INDETERMINATE',
}

enum RecipientSource {
  PAGE = 'page',
  SEARCH = 'search',
}

interface ContentState {
  recipients: ClientGQLDisplay[] | CommunicationRecipientRow[];
  source: RecipientSource;
  isLoading: boolean;
  headerCheckboxStatus: HeaderCheckboxStatus;
  hasAllResults: boolean;
  isZeroState: boolean;
  underTableContent: UnderTableContent;
}

enum UnderTableContent {
  LOADING = 'loading',
  PAGE_ZERO_STATE = 'pageZeroState',
  SEARCH_ZERO_STATE = 'searchZeroState',
  LOAD_MORE_BUTTON = 'loadMoreButton',
  HAS_ALL_RESULTS = 'hasAllResults',
}

const INITIAL_CONTENT_STATE: ContentState = {
  recipients: [],
  source: RecipientSource.PAGE,
  isLoading: true,
  headerCheckboxStatus: HeaderCheckboxStatus.UNCHECKED,
  hasAllResults: false,
  isZeroState: false,
  underTableContent: UnderTableContent.LOADING,
};

@Component({
  selector: 'app-manage-excluded-clients-dialog',
  templateUrl: './manage-excluded-clients-dialog.component.html',
  styleUrls: ['./manage-excluded-clients-dialog.component.scss'],
  standalone: false,
})
export class ManageExcludedClientsDialogComponent implements AfterViewInit, OnDestroy {
  /*********************************************************************************************************************
   *                                      Variables with no in-class dependencies
   ********************************************************************************************************************/

  // Project rule: reactive signal so dev-toolbar overrides take effect at runtime.
  // Declared via inject() to avoid the constructor-only injection-context restriction.
  private readonly _collectionsV2Enabled = inject(LaunchDarklyService).getFeatureFlagSignal(LaunchDarklyFlag.ENABLE_COLLECTIONS_V2);

  get collectionsMode(): CollectionsMode {
    return this.store.selectSnapshot(CommonCollectionsState.getCollectionsMode);
  }

  // make enums available in template
  readonly HeaderCheckboxStatus = HeaderCheckboxStatus;
  readonly LoadableStatus = LoadableStatus;
  readonly ClientSource = RecipientSource;
  readonly UnderTableContent = UnderTableContent;
  readonly CollectionsMode = CollectionsMode;

  // form/data
  recipientSearch = new UntypedFormControl('');
  currentUpdates$ = new BehaviorSubject<ClientGQLDisplay[] | CommunicationRecipientRow[]>([]);

  // table
  @ViewChildren(MatSort) matSort: QueryList<MatSort>; // ViewChild doesn't initialize matSort by ngAfterViewInit
  dataSource = new MatTableDataSource<ClientGQLDisplay | CommunicationRecipientRow>();
  displayedColumns = [
    'exclusionSelection',
    ...(this.collectionsMode === CollectionsMode.CLIENT ? ['clientRef'] : []),
    'name',
    'updateByStaffName',
    'exclusionStatusUpdatedAt',
  ];

  // ol' faithful
  _unsubscribe$ = new Subject<void>();

  private submitted = false;

  /*********************************************************************************************************************
   *                                                Event Observables
   ********************************************************************************************************************/

  // only react to search input changes after debounce time
  searchInputChange$ = getControlValueChanges<string>(this.recipientSearch, true).pipe(
    skip(1),
    tap((term) => this.trackService.setSearchValue(term)),
  );

  // Self-contained: dispatches `SearchExcludedStatementRecipients` directly so the RESULTED
  // emit doesn't depend on `currentClientResults$` being subscribed first (template-render
  // ordering). The action handler is idempotent (LoadableHelper.shouldRequest short-circuits
  // when results are already cached/in-flight), so the duplicate dispatch is a no-op.
  trackSearchInput$ = this.searchInputChange$.pipe(
    filter((term) => this.isSearchTermValid(term)),
    distinctUntilChanged(),
    tap((term) => {
      this.trackService.trackWorkflow(CollectionsExclusionsStep.SEARCH_PERFORMED, {
        searchTermLength: term.length,
      });
      this.store.dispatch(new SearchExcludedStatementRecipients(term));
    }),
    switchMap((term) =>
      this.store.select(CommonCollectionsState.searchExcludedCollectionsClientsStatus(term)).pipe(
        filter((status) => status === LoadableStatus.LOADED),
        take(1),
        withLatestFrom(this.store.select(CommonCollectionsState.searchExcludedCollectionsClients(term))),
        tap(([_, results]) => {
          const resultCount = results?.length ?? 0;
          this.trackService.trackWorkflow(CollectionsExclusionsStep.SEARCH_RESULTED, {
            searchTermLength: term.length,
            resultCount,
            hasResults: resultCount > 0,
          });
        }),
      ),
    ),
    takeUntil(this._unsubscribe$),
  );

  // Submission trigger and reaction
  onSubmitSubject$ = new Subject<void>();
  onSubmit$: Observable<LoadableStatus> = this.onSubmitSubject$.pipe(
    switchMap((_) => this.currentUpdates$),
    switchMap((currentUpdates) => {
      const isV2 = this._collectionsV2Enabled();
      const counts = ManageExcludedClientsDialogComponent.getUpdateCounts(currentUpdates, isV2);
      const startedAt = performance.now();
      const updateRecipientsAction = defer(() => {
        // v1 (must be client)
        if (!isV2) {
          return this.store.dispatch(new UpdateCollectionsClientExclusionStatus(currentUpdates as ClientGQLDisplay[])).pipe(
            switchMap((_) => this.store.select(CommonCollectionsState.getExclusionStatusUpdates)),
            ignoreEmpty(),
            map(({ status, error }) => ({ status, error })),
          );
        }

        // v2 (client or billing contact)
        const exclusionList: CollectionsRecipientExclusion[] = (currentUpdates as CommunicationRecipientRow[]).map(
          typeof (currentUpdates[0] as CommunicationRecipientRow).id === 'string'
            ? ({ id, enabled }) => ({ email: id as string, enable: enabled }) as CollectionsRecipientExclusion
            : ({ id, enabled }) => ({ id: id as number, enable: enabled }) as CollectionsRecipientExclusion,
        );
        // The v2 settings slice stores LoadableStatus only — no error payload — so capture
        // the dispatch error here and thread it through so EXCLUSIONS_SAVE_FAILED carries
        // an `extractFailureContext`-derived errorCategory instead of UNKNOWN.
        return this.store.dispatch(new UpdateCollectionsSettings({ exclusionList })).pipe(
          map(() => ({ error: undefined as unknown })),
          catchError((error: unknown) => of({ error })),
          switchMap(({ error }) =>
            error !== undefined
              ? of({ status: LoadableStatus.ERROR, error })
              : this.store
                  .select(CommonCollectionsState.getCollectionsSetting('updateStatus'))
                  .pipe(map((status) => ({ status, error: undefined as unknown }))),
          ),
        );
      });

      // submit client exclusion status updates
      return updateRecipientsAction.pipe(
        tap(({ status, error }) => {
          if (status === LoadableStatus.ERROR) {
            // Dialog stays open after failure so the user can retry or cancel. Re-open the dismiss
            // funnel by clearing `submitted` — otherwise a post-failure close emits no terminal event.
            this.submitted = false;
            this.trackService.trackWorkflow(CollectionsExclusionsStep.SAVE_FAILED, {
              ...counts,
              ...extractFailureContext(error),
              operationDurationMs: performance.now() - startedAt,
            });
          } else if (status === LoadableStatus.LOADED) {
            this.trackService.trackWorkflow(CollectionsExclusionsStep.SAVE_SUCCEEDED, {
              ...counts,
              operationDurationMs: performance.now() - startedAt,
            });
          }
        }),
        // if only or both submissions were successful
        tap(({ status }) => {
          if (status !== LoadableStatus.ERROR) {
            this.store.dispatch([new ClearExcludedClientsState(), new ClearCollectionsPageData()]);
            this.dialogRef.close(true);
          }
        }),
        map(({ status }) => status),
        startWith(LoadableStatus.LOADING),
      );
    }),
    startWith(LoadableStatus.DEFAULT),
    takeUntil(this._unsubscribe$),
  );

  /**
   * Tracking-shape counts for a save. `v1` rows expose `excludeFromCollections` (true = adding to
   * the exclusion list), v2 rows expose `enabled` (false = adding to the exclusion list).
   */
  static getUpdateCounts(
    updates: ClientGQLDisplay[] | CommunicationRecipientRow[],
    isV2: boolean,
  ): { updateCount: number; addedCount: number; removedCount: number } {
    const addedCount = isV2
      ? (updates as CommunicationRecipientRow[]).filter((row) => !row.enabled).length
      : (updates as ClientGQLDisplay[]).filter((row) => row.excludeFromCollections).length;
    return {
      updateCount: updates.length,
      addedCount,
      removedCount: updates.length - addedCount,
    };
  }

  /*********************************************************************************************************************
   *                                                Store Observables
   ********************************************************************************************************************/

  // get base dependencies for initialization
  initializeExcludedClientStore$ = this.store
    .dispatch(new GetStaff())
    .pipe(switchMap((_) => this.store.dispatch(new GetExcludedStatementRecipients())));

  // latest page cumulative results and status
  pageSource$ = combineLatest([
    this.store.select(CommonCollectionsState.getExcludedCollectionsClients),
    this.store.select(CommonCollectionsState.getLatestExcludedCollectionsClientsPageStatus),
  ]);

  // latest search data and status
  searchSource$(searchTerm: string): Observable<[ClientGQLDisplay[] | CommunicationRecipientRow[], LoadableStatus]> {
    this.store.dispatch(new SearchExcludedStatementRecipients(searchTerm));

    return combineLatest([
      this.store.select(CommonCollectionsState.searchExcludedCollectionsClients(searchTerm)),
      this.store
        .select(CommonCollectionsState.searchExcludedCollectionsClientsStatus(searchTerm))
        .pipe(filter((status) => status !== LoadableStatus.DEFAULT)),
    ]);
  }

  // number of excluded clients taking into account current exclusion status updates
  numberOfExcludedRecipients$ = combineLatest([
    this.launchDarklyService
      .getFeatureFlag(LaunchDarklyFlag.ENABLE_COLLECTIONS_V2)
      .pipe(
        switchMap((isEnabled) =>
          isEnabled
            ? getLoadedResults(this.store.select(CommonCollectionsState.getCollectionsSetting('excludedRecipientIds'))).pipe(
                map((recipientIds) => recipientIds.length),
              )
            : this.store.select(CommonCollectionsState.getAlreadyExcludedCollectionsClientsTotal),
        ),
      ),
    this.currentUpdates$,
  ]).pipe(
    map(([dataSourceLength, currentUpdates]) => {
      const numberOfNewExclusions = currentUpdates.filter(
        this._collectionsV2Enabled()
          ? (currentUpdate: CommunicationRecipientRow) => !currentUpdate.enabled
          : (currentUpdate: ClientGQLDisplay) => currentUpdate.excludeFromCollections,
      ).length;

      return { amount: dataSourceLength + 2 * numberOfNewExclusions - currentUpdates.length };
    }),
  );

  // current client results from either page(s) or search
  currentClientResults$ = merge(
    // page
    this.pageSource$.pipe(
      map(([clients, status]) => ({
        source: RecipientSource.PAGE,
        clients,
        isLoading: status === LoadableStatus.LOADING,
      })),
    ),
    // search or page (based on search term validity)
    this.searchInputChange$.pipe(
      switchMap((searchTerm) => {
        const clientSource$ = this.isSearchTermValid(searchTerm) ? this.searchSource$(searchTerm) : this.pageSource$;
        return clientSource$.pipe(
          map(([clients, status]) => ({
            source: this.isSearchTermValid(searchTerm) ? RecipientSource.SEARCH : RecipientSource.PAGE,
            clients,
            isLoading: status === LoadableStatus.LOADING,
          })),
        );
      }),
    ),
  );

  /*********************************************************************************************************************
   *                                            Component State Observables
   ********************************************************************************************************************/

  // state of table and under table content
  contentState$: Observable<ContentState> = this.initializeExcludedClientStore$.pipe(
    switchMap(() => this.currentClientResults$),
    // once client results are loaded, update table data
    withLatestFrom(
      this.dataSource.connect(),
      this.currentUpdates$,
      this.store.select(CommonCollectionsState.getTotalExcludedCollectionsClientPages),
    ),
    map(([clientResults, dataSourceData, currentUpdates, totalPages]) => {
      let headerCheckboxStatus = HeaderCheckboxStatus.UNCHECKED;
      let combinedClients = dataSourceData as ClientGQLDisplay[] | CommunicationRecipientRow[];

      // update dataSource properties
      if (!clientResults.isLoading) {
        combinedClients = this._collectionsV2Enabled()
          ? this.combineClientDisplaySourcesV2(
              clientResults.clients as CommunicationRecipientRow[],
              currentUpdates as CommunicationRecipientRow[],
              this.dataSource.sort as Sort,
            )
          : this.combineClientDisplaySources(
              clientResults.clients as ClientGQLDisplay[],
              currentUpdates as ClientGQLDisplay[],
              this.dataSource.sort as Sort,
            );

        this.dataSource.data = combinedClients;
        this.dataSource.sortingDataAccessor =
          clientResults.source === RecipientSource.PAGE && !totalPages ? this._sortingDataAccessorDisabled : this._sortingDataAccessor;

        headerCheckboxStatus = this.getHeaderCheckboxStatus(combinedClients);
      }

      return {
        ...clientResults,
        recipients: combinedClients,
        headerCheckboxStatus,
        isZeroState: !combinedClients.length,
        hasAllResults: !!totalPages,
        underTableContent: this.getUnderTableContent(clientResults.source, clientResults.isLoading, !combinedClients.length, !!totalPages),
      };
    }),
    // only needed to initiate table before setting its sort
    startWith(INITIAL_CONTENT_STATE),
  );

  // if any requests return an error status, set corresponding error message
  errorOnLatestRequest$ = merge(
    // page load error
    this.store
      .select(CommonCollectionsState.getLatestExcludedCollectionsClientsPageStatus)
      .pipe(map((status) => (status === LoadableStatus.ERROR ? `Error occurred while retrieving ${this.statementRecipientLabel}s.` : ''))),
    // search error
    this.searchInputChange$.pipe(
      filter((searchTerm) => this.isSearchTermValid(searchTerm)),
      switchMap((searchTerm) => this.store.select(CommonCollectionsState.searchExcludedCollectionsClientsStatus(searchTerm))),
      map((status) => (status === LoadableStatus.ERROR ? `Error occurred while searching for ${this.statementRecipientLabel}s.` : '')),
    ),
    // submission error
    this.store
      .select(CommonCollectionsState.getExclusionStatusUpdates)
      .pipe(map((updates) => (updates?.status === LoadableStatus.ERROR ? 'Error occurred while submitting updates.' : ''))),
  );

  // determine header checkbox state by counting excluded clients in table
  headerCheckboxStatus$ = combineLatest([
    this.dataSource.connect(),
    this.launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_COLLECTIONS_V2),
  ]).pipe(map(([recipients]) => this.getHeaderCheckboxStatus(recipients)));

  statementRecipientHeader = this.collectionsMode === CollectionsMode.CLIENT ? 'Client' : 'Billing contact';

  statementRecipientLabel: StatementRecipientLabel =
    this.collectionsMode === CollectionsMode.CLIENT ? StatementRecipientLabel.CLIENT : StatementRecipientLabel.CONTACT;

  recipientColumnLabel = this.collectionsMode === CollectionsMode.CLIENT ? 'CLIENT' : 'CONTACT EMAIL';

  tableStateRecipientLabel = this.collectionsMode === CollectionsMode.CLIENT ? 'client account' : 'billing contact';

  /*********************************************************************************************************************
   *                                          Component/Class Lifecycle Hooks
   ********************************************************************************************************************/

  constructor(
    private dialogRef: MatDialogRef<ManageExcludedClientsDialogComponent>,
    public readonly configService: ConfigService,
    private readonly launchDarklyService: LaunchDarklyService,
    private store: Store,
    private readonly trackService: CollectionsTrackService,
  ) {
    // Any close without an in-flight save is treated as user-dismiss so analytics can distinguish
    // abandoned dialogs from completed save attempts. SAVE_FAILED leaves the dialog open and resets
    // `submitted` so a subsequent dismiss still emits DIALOG_CLOSED.
    this.dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => {
        if (!this.submitted) {
          this.trackService.trackWorkflow(CollectionsExclusionsStep.DIALOG_CLOSED);
        }
      });
  }

  /**
   * Since matSort is nonstatic, ngOnInit can't be used.
   */
  ngAfterViewInit(): void {
    this.dataSource.sortingDataAccessor = this._sortingDataAccessorDisabled;
    this.dataSource.sort = this.matSort.first;
    this.getOnSortChangeObservable().subscribe();
    this.trackSearchInput$.subscribe();
  }

  /**
   * Clean up subscriptions.
   */
  ngOnDestroy(): void {
    this.trackService.setSearchValue('');
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  /*********************************************************************************************************************
   *                                              Event Callback Functions
   ********************************************************************************************************************/

  getOnSortChangeObservable(): Observable<void> {
    return combineLatest([
      // emit unique sorts after debounce time
      this.matSort.first.sortChange.pipe(
        applicationDebounceTime(),
        distinctUntilChanged(
          (previousSort, currentSort) => previousSort.active === currentSort.active && previousSort.direction === currentSort.direction,
        ),
      ),
      // If sorting occurred on search results and wasn't dismissed before returning to paged results,
      // emit latest sort used in a page query.
      this.searchInputChange$.pipe(
        filter((searchTerm) => this.isSearchTermValid(searchTerm)),
        switchMap(() => this.searchInputChange$),
        filter((searchTerm) => !this.isSearchTermValid(searchTerm)),
        switchMap(() => this.store.select(CommonCollectionsState.getLatestExcludedClientsSort)),
        startWith(undefined as Sort),
      ),
    ]).pipe(
      // if sorting was triggered on paged results or latest sort used in a page query doesn't match current sort
      withLatestFrom(this.searchInputChange$.pipe(startWith(''))),
      filter(
        ([[currentSort, latestSort], searchTerm]) =>
          !this.store.selectSnapshot(CommonCollectionsState.getTotalExcludedCollectionsClientPages) &&
          !this.isSearchTermValid(searchTerm) &&
          !(currentSort.active === latestSort?.active && currentSort.direction === latestSort?.direction),
      ),
      tap(([[sortChange]]) =>
        this.trackService.trackWorkflow(CollectionsExclusionsStep.SORT_CHANGED, {
          sortField: sortChange.active ?? '',
          direction: sortChange.direction || null,
        }),
      ),
      // disable sorting while getting sorted paged results
      switchMap(([[sortChange]]) => {
        this.dataSource.sort.disabled = true;
        return this.store.dispatch(new SortExcludedStatementRecipients(sortChange.direction ? sortChange : undefined)).pipe(
          take(1),
          finalize(() => (this.dataSource.sort.disabled = false)),
        );
      }),
      takeUntil(this._unsubscribe$),
    );
  }

  onSubmit(): void {
    const isV2 = this._collectionsV2Enabled();
    this.submitted = true;
    this.trackService.trackWorkflow(
      CollectionsExclusionsStep.SAVE_SUBMITTED,
      ManageExcludedClientsDialogComponent.getUpdateCounts(this.currentUpdates$.value, isV2),
    );
    this.onSubmitSubject$.next();
  }

  onHeaderCheckboxChange(checked: boolean): void {
    this.trackService.trackWorkflow(CollectionsExclusionsStep.HEADER_TOGGLED, { selectedAll: checked });
    this.currentUpdates$.next(
      this.dataSource.data.reduce(
        this._collectionsV2Enabled()
          ? (updates: CommunicationRecipientRow[], recipient: CommunicationRecipientRow) =>
              this.getUpdatedSelectionsV2(!checked, recipient, updates)
          : (updates: ClientGQLDisplay[], client: ClientGQLDisplay) => this.getUpdatedSelections(checked, client, updates),
        this.currentUpdates$.value,
      ),
    );
  }

  onCellCheckboxChange(recipient: ClientGQLDisplay | CommunicationRecipientRow): void {
    const toggledCheckboxResult = this.getToggledCheckboxResult(recipient);
    const isV2 = this._collectionsV2Enabled();
    this.trackService.trackWorkflow(CollectionsExclusionsStep.RECIPIENT_TOGGLED, {
      // v1: toggledCheckboxResult=true means excluded; v2: toggledCheckboxResult=true means enabled
      excluded: isV2 ? !toggledCheckboxResult : toggledCheckboxResult,
    });

    const updatedSelections = this._collectionsV2Enabled()
      ? this.getUpdatedSelectionsV2(
          toggledCheckboxResult,
          recipient as CommunicationRecipientRow,
          this.currentUpdates$.value as CommunicationRecipientRow[],
        )
      : this.getUpdatedSelections(toggledCheckboxResult, recipient as ClientGQLDisplay, this.currentUpdates$.value as ClientGQLDisplay[]);

    this.currentUpdates$.next(updatedSelections);
  }

  /**
   * This function is a bit weird. Bottom line, it toggles between checked/unchecked based on existing and current values. The issue is the
   * field representing this state is inversed between v1 and v2, so the return value represents excluded or enabled based on version.
   * ```
   * v1: true = excluded
   * v2: true = enabled
   * ```
   * @param recipient
   */
  getToggledCheckboxResult(recipient: ClientGQLDisplay | CommunicationRecipientRow): boolean {
    if (this._collectionsV2Enabled()) {
      const matchingUpdate = this.currentUpdates$.value.find(
        ({ id }: CommunicationRecipientRow) => id === (recipient as CommunicationRecipientRow).id,
      ) as CommunicationRecipientRow;

      return !(matchingUpdate?.enabled ?? (recipient as CommunicationRecipientRow).enabled);
    }

    const matchingUpdate = this.currentUpdates$.value.find(
      ({ clientRef }: ClientGQLDisplay) => clientRef === (recipient as ClientGQLDisplay).clientRef,
    ) as ClientGQLDisplay;

    return !(matchingUpdate?.excludeFromCollections ?? (recipient as ClientGQLDisplay).excludeFromCollections);
  }

  onLoadMoreButtonClick(): void {
    const nextPage = this.store.selectSnapshot(CommonCollectionsState.getLatestExcludedCollectionsClientsPageNumber) + 1;
    this.trackService.trackWorkflow(CollectionsExclusionsStep.LOAD_MORE_CLICKED, { page: nextPage });
    this.store.dispatch(
      new GetExcludedStatementRecipients(
        nextPage,
        this.dataSource.sort?.direction
          ? {
              active: this.dataSource.sort.active,
              direction: this.dataSource.sort.direction,
            }
          : undefined,
      ),
    );
  }

  onClose(): void {
    this.dialogRef.close();
  }

  /*********************************************************************************************************************
   *                                             Data Management Functions
   ********************************************************************************************************************/

  getUpdatedSelections(exclusionStatus: boolean, client: ClientGQLDisplay, currentUpdates: ClientGQLDisplay[]): ClientGQLDisplay[] {
    // toggle client exclusion status
    if (client.excludeFromCollections !== exclusionStatus) {
      client = {
        ...client,
        excludeFromCollections: exclusionStatus,
      };
      this.dataSource.data = (this.dataSource.data as ClientGQLDisplay[]).map((clientInstance) =>
        clientInstance.clientRef === client.clientRef ? client : clientInstance,
      );

      // remove client from selections if toggle returns exclusion status to initial value, add otherwise
      const matchingIndex = currentUpdates.map((currentUpdate) => currentUpdate.clientRef).indexOf(client.clientRef);

      if (matchingIndex === -1) {
        return currentUpdates.concat(client);
      }
      currentUpdates.splice(matchingIndex, 1);
    }
    return currentUpdates;
  }

  getUpdatedSelectionsV2(
    enabled: boolean,
    recipient: CommunicationRecipientRow,
    currentUpdates: CommunicationRecipientRow[],
  ): CommunicationRecipientRow[] {
    if (recipient.enabled === enabled) {
      return currentUpdates;
    }

    // toggle client exclusion status
    recipient = { ...recipient, enabled };

    // update table data
    const matchingDatasourceIndex = (this.dataSource.data as CommunicationRecipientRow[]).findIndex(({ id }) => id === recipient.id);
    this.dataSource.data = replaceArrayItem(this.dataSource.data, matchingDatasourceIndex, recipient);

    // remove client from selections if toggle returns exclusion status to initial value, add otherwise
    const matchingUpdatesIndex = currentUpdates.findIndex(({ id }) => id === recipient.id);
    return matchingUpdatesIndex === -1 ? currentUpdates.concat(recipient) : removeArrayItem(currentUpdates, matchingUpdatesIndex);
  }

  combineClientDisplaySources(
    clientsFromState: ClientGQLDisplay[],
    clientsWithUpdates: ClientGQLDisplay[],
    sort?: Sort,
  ): ClientGQLDisplay[] {
    let clientsToReturn = clientsFromState;

    if (clientsWithUpdates.length) {
      clientsToReturn = clientsFromState.map((client) => {
        const matchingClientIndex = clientsWithUpdates.findIndex((updatedClient) => client.clientRef === updatedClient.clientRef);
        if (matchingClientIndex !== -1) {
          return clientsWithUpdates[matchingClientIndex];
        }
        return client;
      });
    }

    clientsToReturn = clientsToReturn.concat(
      clientsWithUpdates.filter((client) => !clientsToReturn.find((clientToReturn) => clientToReturn.clientRef === client.clientRef)),
    );

    if (sort) {
      return clientsToReturn.sort((firstClient, secondClient) => {
        if (firstClient[sort.active] > secondClient[sort.active]) {
          return sort.direction === 'asc' ? 1 : -1;
        } else if (firstClient[sort.active] < secondClient[sort.active]) {
          return sort.direction === 'asc' ? -1 : 1;
        }
        return 1;
      });
    }

    return clientsToReturn;
  }

  combineClientDisplaySourcesV2(
    recipients: CommunicationRecipientRow[],
    updatedRecipients: CommunicationRecipientRow[],
    sort?: Sort,
  ): CommunicationRecipientRow[] {
    const combinedRecipients = (updatedRecipients ?? []).reduce((acc, clientWithUpdates) => {
      const matchingRecipientIndex = recipients.findIndex(({ id }) => id === clientWithUpdates.id);
      return matchingRecipientIndex !== -1 ? replaceArrayItem(acc, matchingRecipientIndex, clientWithUpdates) : acc;
    }, recipients);

    if (!sort) {
      return combinedRecipients;
    }

    return combinedRecipients.sort((firstClient, secondClient) => {
      if (firstClient[sort.active] > secondClient[sort.active]) {
        return sort.direction === 'asc' ? 1 : -1;
      } else if (firstClient[sort.active] < secondClient[sort.active]) {
        return sort.direction === 'asc' ? -1 : 1;
      }
      return 1;
    });
  }

  getUnderTableContent(source: RecipientSource, isLoading: boolean, isZeroState: boolean, hasAllResults: boolean): UnderTableContent {
    if (isLoading) {
      return UnderTableContent.LOADING;
    }

    if (isZeroState) {
      return source === RecipientSource.PAGE ? UnderTableContent.PAGE_ZERO_STATE : UnderTableContent.SEARCH_ZERO_STATE;
    }

    if (source === RecipientSource.SEARCH) {
      return undefined;
    }

    return hasAllResults ? UnderTableContent.HAS_ALL_RESULTS : UnderTableContent.LOAD_MORE_BUTTON;
  }

  getHeaderCheckboxStatus(clients: (ClientGQLDisplay | CommunicationRecipientRow)[]): HeaderCheckboxStatus {
    const numberOfCheckedRows = (clients ?? []).filter(
      this._collectionsV2Enabled()
        ? (client: CommunicationRecipientRow) => !client.enabled
        : (client: ClientGQLDisplay) => client.excludeFromCollections,
    ).length;

    if (numberOfCheckedRows === clients.length) {
      return HeaderCheckboxStatus.CHECKED;
    } else if (numberOfCheckedRows === 0) {
      return HeaderCheckboxStatus.UNCHECKED;
    }

    return HeaderCheckboxStatus.INDETERMINATE;
  }

  /*********************************************************************************************************************
   *                                              Miscellaneous Functions
   ********************************************************************************************************************/

  isSearchTermValid(searchTerm: string): boolean {
    return searchTerm.length > 1;
  }

  _sortingDataAccessor(data: ClientGQLDisplay, sortHeaderID: string): string {
    return typeof data[sortHeaderID] === 'string' ? data[sortHeaderID].toLocaleLowerCase() : data[sortHeaderID];
  }

  _sortingDataAccessorDisabled(..._: unknown[]): string {
    return '';
  }
}
